"use client";
import { motion } from "framer-motion";

const cards = [
  {
    key: "bot",
    icon: "/icons/JA Full-Color_GEAR.png",
    title: "INJAZ Bot",
    badge: "For platform help",
    description: "Use INJAZ Bot if you need support with registration, login, account access, or navigating the platform.",
    bullets: [
      "Registration and sign-up help",
      "Platform navigation support",
      "Account and technical questions",
    ],
    iconRotate: -20,
  },
  {
    key: "coach",
    icon: "/icons/JA Full-Color_PREPARE - BRAIN.png",
    title: "AI Coach",
    badge: "For learning support",
    description: "Use AI Coach to better understand financial concepts, think through examples, and strengthen your learning as you go.",
    bullets: [
      "Financial concept explanations",
      "Guided thinking and examples",
      "Real-life money scenarios",
    ],
    iconRotate: 20,
  },
];

const chamfer = "polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))";

export default function AiFeatures() {
  return (
    <section
      id="support"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url('/images/landing/support-bg-texture.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-secondary/60 pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Support when you need it
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed">
            You are not learning alone. The platform includes built-in support tools to help you
            move forward with confidence.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card, idx) => (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              whileHover={{ y: -3, transition: { duration: 0.2 } }}
              className="bg-white border border-white/60 shadow-lg"
              style={{ clipPath: chamfer }}
            >
              <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-border">
                {/* Left: icon + title + badge */}
                <div className="md:w-56 shrink-0 flex flex-col justify-center items-center md:items-start gap-3 px-8 py-6">
                  <motion.img
                    src={card.icon}
                    alt=""
                    width={96}
                    height={96}
                    initial={{ rotate: card.iconRotate, opacity: 0, scale: 0.7 }}
                    whileInView={{ rotate: 0, opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 + idx * 0.1 }}
                  />
                  <h3 className="font-bold text-xl text-foreground leading-tight">{card.title}</h3>
                </div>

                {/* Right: description + bullets */}
                <div className="flex-1 px-8 py-6">
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {card.description}
                  </p>
                  <ul className="space-y-2">
                    {card.bullets.map((item, i) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: -8 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.3 + i * 0.07 }}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <span className="text-primary mt-0.5 shrink-0">•</span>
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
